import { useEffect, useMemo, useRef, useState } from 'react';
import { ExternalLink, MapPin, Minus, Plus, Search } from 'lucide-react';

interface MapCoordinatePickerProps {
  latitude: number | null | undefined;
  longitude: number | null | undefined;
  onCoordinatesChange?: (lat: number | null, lng: number | null) => void;
  onChange?: (lat: number | null, lng: number | null) => void;
  label?: string;
  disabled?: boolean;
}

interface LatLngLike {
  lat: number;
  lng: number;
}

interface PlacesSearchResult {
  placeId: string;
  name: string;
  address: string;
  latitude: number;
  longitude: number;
  mapsUrl: string;
}

const DEFAULT_POSITION: LatLngLike = { lat: 40.1689, lng: 15.1428 };
const STATIC_MAP_WIDTH = 640;
const STATIC_MAP_HEIGHT = 400;
const TILE_SIZE = 256;
const MIN_ZOOM = 3;
const MAX_ZOOM = 20;

const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const normalizeLatLng = (lat: number, lng: number): LatLngLike | null => {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
  return { lat, lng };
};

const isNullIslandLike = (lat: number, lng: number) => Math.abs(lat) < 0.05 && Math.abs(lng) < 0.05;

const parseLatLngFromText = (value: string): LatLngLike | null => {
  const text = value.trim();
  if (!text) return null;

  const decodedCandidates = new Set<string>([text]);
  try {
    decodedCandidates.add(decodeURIComponent(text));
  } catch {
    // Keep parsing the raw input.
  }

  for (const candidate of decodedCandidates) {
    const patterns = [
      /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)(?:[,/?&]|$)/,
      /[?&](?:q|query|ll|center)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)(?:[&]|$)/,
      /!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/,
      /(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
    ];

    for (const pattern of patterns) {
      const match = candidate.match(pattern);
      if (!match) continue;
      const parsed = normalizeLatLng(Number.parseFloat(match[1]), Number.parseFloat(match[2]));
      if (parsed) return parsed;
    }
  }

  return null;
};

const latLngToPixel = (latLng: LatLngLike, zoom: number) => {
  const sinLat = clamp(Math.sin((latLng.lat * Math.PI) / 180), -0.9999, 0.9999);
  const scale = TILE_SIZE * (2 ** zoom);
  return {
    x: ((latLng.lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI)) * scale,
  };
};

const pixelToLatLng = (pixel: { x: number; y: number }, zoom: number): LatLngLike => {
  const scale = TILE_SIZE * (2 ** zoom);
  const lng = (pixel.x / scale) * 360 - 180;
  const n = Math.PI - (2 * Math.PI * pixel.y) / scale;
  const lat = (180 / Math.PI) * Math.atan(0.5 * (Math.exp(n) - Math.exp(-n)));
  return {
    lat: clamp(lat, -85.05112878, 85.05112878),
    lng: ((((lng + 180) % 360) + 360) % 360) - 180,
  };
};

const MapCoordinatePicker = ({
  latitude,
  longitude,
  onCoordinatesChange,
  onChange,
  label = 'Coordinate',
  disabled = false,
}: MapCoordinatePickerProps) => {
  const hasCoordinates = typeof latitude === 'number' && Number.isFinite(latitude)
    && typeof longitude === 'number' && Number.isFinite(longitude)
    && !isNullIslandLike(latitude, longitude);
  const selectedPosition = hasCoordinates
    ? { lat: latitude as number, lng: longitude as number }
    : DEFAULT_POSITION;

  const [showMap, setShowMap] = useState(false);
  const [searchAddress, setSearchAddress] = useState('');
  const [zoom, setZoom] = useState(16);
  const [requestedZoom, setRequestedZoom] = useState(16);
  const [mapCenter, setMapCenter] = useState<LatLngLike>(selectedPosition);
  const [mapImageLoaded, setMapImageLoaded] = useState(false);
  const [mapImageError, setMapImageError] = useState(false);
  const [lastGoodMapUrl, setLastGoodMapUrl] = useState('');
  const [placesResults, setPlacesResults] = useState<PlacesSearchResult[]>([]);
  const [placesLoading, setPlacesLoading] = useState(false);
  const [placesError, setPlacesError] = useState('');
  const mapImageRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    if (!hasCoordinates) return;
    setMapCenter({ lat: latitude as number, lng: longitude as number });
  }, [hasCoordinates, latitude, longitude]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setZoom(requestedZoom);
    }, 180);
    return () => window.clearTimeout(timer);
  }, [requestedZoom]);

  const handleCoordinateChange = (lat: number | null, lng: number | null) => {
    if (onCoordinatesChange) onCoordinatesChange(lat, lng);
    if (onChange) onChange(lat, lng);
  };

  const applyPosition = (position: LatLngLike) => {
    const normalized = normalizeLatLng(position.lat, position.lng);
    if (!normalized) return;
    setMapCenter(normalized);
    handleCoordinateChange(normalized.lat, normalized.lng);
  };

  const staticMapUrl = useMemo(() => {
    const params = new URLSearchParams({
      center: `${mapCenter.lat},${mapCenter.lng}`,
      zoom: String(zoom),
      size: `${STATIC_MAP_WIDTH}x${STATIC_MAP_HEIGHT}`,
      scale: '2',
      maptype: 'roadmap',
      language: 'it',
      markers: `color:red|${selectedPosition.lat},${selectedPosition.lng}`,
    });
    return `/api/maps-static?${params.toString()}`;
  }, [mapCenter.lat, mapCenter.lng, selectedPosition.lat, selectedPosition.lng, zoom]);

  useEffect(() => {
    setMapImageLoaded(false);
    setMapImageError(false);
  }, [staticMapUrl]);

  const googleExternalUrl = hasCoordinates
    ? `https://www.google.com/maps?q=${latitude},${longitude}`
    : `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchAddress.trim() || label || 'Google Maps')}`;

  const handleStaticMapClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (disabled) return;
    if (!mapImageLoaded || mapImageError) return;
    const rect = mapImageRef.current?.getBoundingClientRect();
    if (!rect || rect.width <= 0 || rect.height <= 0) return;
    if (
      event.clientX < rect.left
      || event.clientX > rect.right
      || event.clientY < rect.top
      || event.clientY > rect.bottom
    ) {
      return;
    }
    const relativeX = ((event.clientX - rect.left) / rect.width) * STATIC_MAP_WIDTH;
    const relativeY = ((event.clientY - rect.top) / rect.height) * STATIC_MAP_HEIGHT;
    const centerPixel = latLngToPixel(mapCenter, zoom);
    const clickedPixel = {
      x: centerPixel.x + relativeX - STATIC_MAP_WIDTH / 2,
      y: centerPixel.y + relativeY - STATIC_MAP_HEIGHT / 2,
    };
    applyPosition(pixelToLatLng(clickedPixel, zoom));
  };

  const handleSearchAddress = () => {
    if (!searchAddress.trim()) return;

    const parsedLatLng = parseLatLngFromText(searchAddress);
    if (parsedLatLng) {
      setRequestedZoom((current) => Math.max(current, 17));
      applyPosition(parsedLatLng);
      return;
    }

    void searchPlaces(searchAddress.trim());
  };

  const searchPlaces = async (query: string) => {
    const normalizedQuery = query.trim();
    if (normalizedQuery.length < 3) return;
    setPlacesLoading(true);
    setPlacesError('');
    try {
      const params = new URLSearchParams({
        query: normalizedQuery,
        language: 'it',
        limit: '5',
      });
      if (hasCoordinates) {
        params.set('lat', String(selectedPosition.lat));
        params.set('lng', String(selectedPosition.lng));
      }
      const response = await fetch(`/api/maps-places-search?${params.toString()}`, {
        method: 'GET',
        headers: { Accept: 'application/json' },
      });
      const parsed = await response.json().catch(() => null);
      if (!response.ok || !parsed?.success) {
        throw new Error(parsed?.error || `HTTP ${response.status}`);
      }
      const results = Array.isArray(parsed.results) ? parsed.results : [];
      setPlacesResults(results);
      if (results.length === 0) {
        setPlacesError('Nessun luogo trovato. Puoi aprire Google Maps o inserire coordinate manualmente.');
      } else {
        const first = results[0];
        if (Number.isFinite(first.latitude) && Number.isFinite(first.longitude)) {
          setRequestedZoom((current) => Math.max(current, 17));
          setMapCenter({ lat: first.latitude, lng: first.longitude });
        }
      }
    } catch {
      setPlacesResults([]);
      setPlacesError('Ricerca Places non disponibile. Puoi aprire Google Maps o inserire coordinate manualmente.');
    } finally {
      setPlacesLoading(false);
    }
  };

  const applyPlace = (place: PlacesSearchResult) => {
    const normalized = normalizeLatLng(Number(place.latitude), Number(place.longitude));
    if (!normalized) return;
    setSearchAddress(place.address || place.name || searchAddress);
    setRequestedZoom((current) => Math.max(current, 17));
    applyPosition(normalized);
  };

  return (
    <div>
      <label className="mb-2 block text-sm font-medium text-gray-700">
        <MapPin size={16} className="mr-2 inline" />
        {label}
      </label>

      <div className="mb-3 grid grid-cols-2 gap-4">
        <div>
          <label className="mb-1 block text-xs text-gray-600">Latitudine</label>
          <input
            type="number"
            step="any"
            value={latitude ?? ''}
            onChange={(e) => {
              const nextLat = e.target.value === '' ? null : Number.parseFloat(e.target.value);
              const validLat = Number.isFinite(nextLat as number) ? (nextLat as number) : null;
              handleCoordinateChange(validLat, longitude ?? null);
              if (validLat != null && typeof longitude === 'number' && Number.isFinite(longitude)) {
                setMapCenter({ lat: validLat, lng: longitude });
              }
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="40.1689"
            disabled={disabled}
          />
        </div>
        <div>
          <label className="mb-1 block text-xs text-gray-600">Longitudine</label>
          <input
            type="number"
            step="any"
            value={longitude ?? ''}
            onChange={(e) => {
              const nextLng = e.target.value === '' ? null : Number.parseFloat(e.target.value);
              const validLng = Number.isFinite(nextLng as number) ? (nextLng as number) : null;
              handleCoordinateChange(latitude ?? null, validLng);
              if (typeof latitude === 'number' && Number.isFinite(latitude) && validLng != null) {
                setMapCenter({ lat: latitude, lng: validLng });
              }
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
            placeholder="15.1428"
            disabled={disabled}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={() => setShowMap(!showMap)}
        disabled={disabled}
        className="mb-3 flex w-full items-center justify-center space-x-2 rounded-lg bg-primary/10 px-4 py-2 text-primary transition-colors hover:bg-primary/20 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-500"
      >
        <MapPin size={16} />
        <span>{showMap ? 'Nascondi Mappa' : 'Seleziona su Mappa'}</span>
      </button>

      {hasCoordinates && !disabled && (
        <button
          type="button"
          onClick={() => handleCoordinateChange(null, null)}
          className="mb-3 w-full rounded-lg border border-red-200 px-4 py-2 text-sm text-red-600 transition-colors hover:bg-red-50"
        >
          Rimuovi coordinate
        </button>
      )}

      {showMap && (
        <div className="overflow-hidden rounded-lg border border-gray-300">
          <div className="border-b border-gray-300 bg-gray-50 p-3">
            <div className="flex gap-2">
              <input
                type="text"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchAddress()}
                placeholder="Incolla link Google Maps o coordinate lat,lng"
                className="min-w-0 flex-1 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary focus:ring-2 focus:ring-primary/20"
              />
              <button
                type="button"
                onClick={handleSearchAddress}
                className="rounded-lg bg-primary px-4 py-2 text-white transition-colors hover:bg-primary/90"
              >
                <Search size={16} />
              </button>
            </div>
            <p className="mt-2 text-xs text-gray-600">
              Clicca sulla mappa statica Google, incolla un link Google Maps oppure cerca un luogo con Places.
            </p>
            {placesLoading && (
              <p className="mt-2 text-xs text-gray-600">Ricerca Places in corso...</p>
            )}
            {placesError && (
              <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                <span>{placesError}</span>
                <button
                  type="button"
                  onClick={() => window.open(
                    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(searchAddress.trim())}`,
                    '_blank',
                    'noopener,noreferrer',
                  )}
                  className="shrink-0 text-primary hover:underline"
                >
                  Apri Maps
                </button>
              </div>
            )}
            {placesResults.length > 0 && (
              <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-gray-200 bg-white">
                {placesResults.map((place) => (
                  <button
                    key={place.placeId || `${place.name}-${place.latitude}-${place.longitude}`}
                    type="button"
                    onClick={() => applyPlace(place)}
                    className="block w-full border-b border-gray-100 px-3 py-2 text-left last:border-b-0 hover:bg-primary/5"
                  >
                    <span className="block text-sm font-medium text-gray-800">{place.name}</span>
                    <span className="block text-xs text-gray-600">{place.address}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="relative bg-gray-100">
            <button
              type="button"
              onClick={handleStaticMapClick}
              disabled={disabled || !mapImageLoaded || mapImageError}
              className="relative block aspect-[8/5] w-full cursor-crosshair overflow-hidden disabled:cursor-not-allowed"
              aria-label="Seleziona coordinate sulla mappa"
            >
              <img
                ref={mapImageRef}
                src={mapImageError && lastGoodMapUrl ? lastGoodMapUrl : staticMapUrl}
                alt="Mappa Google per selezionare coordinate"
                className="absolute inset-0 h-full w-full select-none object-fill"
                draggable={false}
                onLoad={() => {
                  setMapImageLoaded(true);
                  setMapImageError(false);
                  setLastGoodMapUrl(staticMapUrl);
                }}
                onError={() => {
                  setMapImageLoaded(false);
                  setMapImageError(true);
                }}
              />
            </button>
            {!mapImageLoaded && !mapImageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 text-sm text-gray-600">
                Caricamento mappa…
              </div>
            )}
            {mapImageError && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-50 px-4 text-center text-sm text-red-600">
                Mappa non disponibile. Inserisci coordinate o incolla un link Google Maps.
              </div>
            )}
            <div className="absolute right-3 top-3 flex overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
              <button
                type="button"
                onClick={() => setRequestedZoom((current) => clamp(current + 1, MIN_ZOOM, MAX_ZOOM))}
                className="border-r border-gray-200 p-2 text-gray-700 hover:bg-gray-50"
                aria-label="Aumenta zoom"
              >
                <Plus size={16} />
              </button>
              <button
                type="button"
                onClick={() => setRequestedZoom((current) => clamp(current - 1, MIN_ZOOM, MAX_ZOOM))}
                className="p-2 text-gray-700 hover:bg-gray-50"
                aria-label="Riduci zoom"
              >
                <Minus size={16} />
              </button>
            </div>
          </div>

          <div className="border-t border-gray-300 bg-gray-50 p-3">
            {hasCoordinates ? (
              <>
                <p className="text-sm text-gray-700">
                  <strong>Coordinate selezionate:</strong> {(latitude as number).toFixed(6)}, {(longitude as number).toFixed(6)}
                </p>
                <a
                  href={googleExternalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                >
                  Apri in Google Maps <ExternalLink size={13} />
                </a>
              </>
            ) : (
              <p className="text-sm text-gray-700">
                <strong>Coordinate selezionate:</strong> nessun punto impostato
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MapCoordinatePicker;
